import React, { useEffect, useState } from "react";
import { Accordion, Button, Card } from "react-bootstrap";
import "./OpalOrders.css";
import TauriLoader2 from "../../../TauriLoader2/TauriLoader2";
import TauriImage from "../../../TauriImage/TauriImage";
import { useBaseProvider } from "../../../../BaseManager/BaseManager";
import { addRecord, deleteRecord, deleteRecordByField, getAll, getNextId, getRecordById, updateRecord } from "../../../../localDB";
import OpalDataTable from "../../../OpalDataTable/OpalDataTable";
import { orderStatus, statusMapping, SyncStatusMap } from "../../../../visualizations/visualizations";
import EditOrderModal from "../../../OpalOrderLayout/EditOrderModal/EditOrderModal";
import OrderDetailsModal from "../../../OpalOrderLayout/OrderDetailsModal/OrderDetailsModal";
import OpalAlertModal from "../../../OpalAlertModal/OpalAlertModal";
import appURL from '../../../../appURL.json';
import { transformOrders, upsertLocalDataOrders } from "../../../OpalOrderLayout/OrderFactoryData/OrderFactoryData";

const OpalOrders = ({ section, id, callPhase }) => {

    const sessionIdRef = React.useRef(id);

    const [loading, setIsLoading] = useState(false);
    const [alertLoading, setAlertLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [account, setAccount] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showEditOrder, setShowEditOrder] = useState(false);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
    const [session, setSession] = useState(null);

    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalProps, setAlertModalProps] = useState(null);

    const { isSync } = useBaseProvider();

    const orderStatusName = Object.fromEntries(
        Object.entries(orderStatus).map(([key, value]) => [value, key])
    );

    const orderColumns = [
        {
            key: "Status",
            label: "STATUS",
            render: row => orderStatusName[row.Status]
        },
        { key: "OrderDate", label: "ORDER DATE" },
        { key: "OrderNumber", label: "ORDER NO." },
        { key: "NetTotal", label: "NET AMOUNT", inputType: "number" }
    ];

    const orderActions = [
        {
            label: "Details",
            onClick: row => handleOrderDetails(row)
        },
        {
            label: "Edit",
            onClick: row => handleEditOrder(row)
        },
        {
            label: "Cancel",
            onClick: row => cancelOrderConfirmation(row)
        }
    ];


    useEffect(() => {
        const fetchInitialData = async () => {
            await fetchOrders();
        };

        if (id) fetchInitialData();
    }, [id]);

    useEffect(() => {
        sessionIdRef.current = id;
    }, [id]);

    useEffect(() => {
        const handleRefresh = () => {
            if (id) fetchOrders();
        };

        window.addEventListener("refreshSession", handleRefresh);

        return () => {
            window.removeEventListener("refreshSession", handleRefresh);
        };
    }, [id]);

    const fetchOrders = async () => {
        setIsLoading(true);

        const currentSessionId = sessionIdRef.current;

        try {

            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                const token = localStorage.getItem('token');
                const devId = localStorage.getItem('devId');

                const response = await fetch(`${appURL.API_GetSessionOrders}/${currentSessionId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-ETR-DeviceID': devId
                    }
                });

                if (!response.ok) {
                    console.error('Failed to get orders');
                    return;
                }

                const data = await response.json();

                if (currentSessionId !== sessionIdRef.current) return;

                const transformedOrders = await transformOrders(data.orders || null);
                await upsertLocalDataOrders(transformedOrders);
            }

            await loadLocalOrders();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const loadLocalOrders = async () => {
        try {
            const orders = await getAll("Orders");
            const sessionId = Number(sessionIdRef.current);
            const _orders = orders.filter(a => a.SessionID === sessionId && a.SyncStatus !== SyncStatusMap.done);

            const session = await getRecordById('session', sessionId);
            const account = await getRecordById('customer', session?.AccountID);
            setAccount(account);
            setSession(session);
            setOrders(_orders);
        } catch (err) {
            console.log(err);
        }
    }

    const handleCreateOrder = async () => {
        setAlertLoading(true);

        try {
            const timestamp = Date.now();
            const combinedId = Number(`${timestamp}`);

            const order = {
                ID: combinedId,
                OrderNumber: "",
                OrderDate: new Date().toISOString().split('T')[0],
                SessionID: id,
                Account: {
                    ID: account?.ID,
                    Name: account?.Name
                },
                GrossTotal: 0,
                TotalDiscount: 0,
                NetTotal: 0,
                DeliveryAddress: account?.AddressDetail,
                Remarks: "",
                Authorizer: "",
                Status: orderStatus.Draft,
                SyncStatus: SyncStatusMap.create
            };

            await addRecord("Orders", order);

            setSelectedRow({ ID: combinedId });
            setShowEditOrder(true);
            await loadLocalOrders();
            setShowAlertModal(false);
        } catch (error) {
            console.log(error);
        } finally {
            setAlertLoading(false);
        }
    }

    const handleRowClick = (row) => {

    };

    const handleEditOrder = async (row) => {

        const fresh = await getRecordById("Orders", row.ID);

        if (row?.Status !== orderStatus.Draft) {

            const message = {
                title: "Edit Not Allowed",
                bodyText: "This order can only be edited while it is in Draft status.",
                modalType: "message"
            }

            setAlertModalProps(message);
            setShowAlertModal(true);

            return;
        }

        setSelectedRow({ ID: fresh.ID });
        setShowEditOrder(true);
    };

    const handleOrderDetails = (row) => {
        setSelectedRow(row);
        setShowOrderDetailsModal(true);
    };

    const cancelOrderConfirmation = (row) => {
        if (row?.Status !== orderStatus.Draft) {

            const message = {
                title: "Cancel Not Allowed",
                bodyText: "This order can only be cancelled in Draft status. Please contact an administrator if the order is already in process.",
                modalType: "message"
            }

            setAlertModalProps(message);
            setShowAlertModal(true);

            return;
        }

        const message = {
            title: "Message",
            bodyText: "Do you want to cancel this order?",
            onConfirm: () => handleCancelOrder(row?.ID),
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            modalType: "question"
        }

        setAlertModalProps(message);
        setShowAlertModal(true);

        return;
    }

    const hasDraftOrder = orders.some(order => order.Status === orderStatus.Draft);

    const handleCancelOrder = async (OrderID) => {
        setAlertLoading(true);
        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const lOrder = await getRecordById("Orders", OrderID);

            await updateRecord("Orders", {
                ...lOrder,
                Status: orderStatus.Cancelled
            });

            setShowAlertModal(false);
            await loadLocalOrders();
        } catch (error) {
            console.log(error);
        } finally {
            setAlertLoading(false);
        }
    }

    const handleClickNewOrder = () => {
        const isInvalidAccount =
            !account || account.Code === 'INC-ACCOUNT';

        if (isInvalidAccount) {
            setAlertModalProps({
                title: "Validation",
                bodyText: "This account is marked as Incidental and is not yet assigned to your universe.",
                modalType: "message"
            });

            setShowAlertModal(true);
            return;
        }

        setAlertModalProps({
            title: "Message",
            bodyText: "Do you want to create a new order?",
            onConfirm: () => handleCreateOrder(),
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            modalType: "question"
        });

        setShowAlertModal(true);
    };

    return (
        <div className="opal-orders mb-2">
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header className="p-0 mb-2">
                        <div className="header-container">
                            <div className="header-left">
                                <TauriImage localImage={section.icon} className="tat-icon me-2" />
                                <span className="header-title">{section?.header.toUpperCase()}</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <Button
                                    className="btn d-flex align-items-center p-0 m-0 me-2"
                                    disabled={session?.Status !== statusMapping.inprogress || isSync || loading || hasDraftOrder}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClickNewOrder();
                                    }}>
                                    <TauriImage localImage={"/Images/material-symbols--format-list-bulleted-add.svg"} className={"me-1"} />
                                    <span>New Order</span>
                                </Button>
                                <div className="item-count">{orders?.length} items</div>
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className="py-0 px-0">
                        {loading ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <OpalDataTable
                                loading={loading}
                                data={orders}
                                columns={orderColumns}
                                actions={orderActions}
                                onRowClick={handleRowClick}
                            />
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <EditOrderModal
                show={showEditOrder}
                onHide={(changed) => {
                    setShowEditOrder(false);
                    if (changed) {
                        loadLocalOrders();
                    }
                }}
                isCoverage={true}
                id={selectedRow?.ID}
            />
            <OrderDetailsModal
                show={showOrderDetailsModal}
                onHide={() => setShowOrderDetailsModal(false)}
                id={selectedRow?.ID}
            />
            <OpalAlertModal
                show={showAlertModal}
                onHide={() => setShowAlertModal(false)}
                title={alertModalProps?.title}
                bodyText={alertModalProps?.bodyText}
                confirmButtonText={alertModalProps?.confirmButtonText}
                cancelButtonText={alertModalProps?.cancelButtonText}
                onConfirm={alertModalProps?.onConfirm}
                modalType={alertModalProps?.modalType}
                Icon={alertModalProps?.Icon}
                loading={alertLoading}
            />
        </div>
    );
};

export default OpalOrders;